import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { usePrevious } from '../../hooks/usePrevious';
import {
    AgGridEvent,
    ClientSideRowModelModule,
    ColumnResizedEvent,
    FilterChangedEvent,
    GridApi,
    GridReadyEvent,
    SelectionChangedEvent,
} from 'ag-grid-community';
import { Distributor } from '../../../core/domainModels/distributors/Distributor';
import { distributorsTableColumnsDefinitions, distributorsTableDefaultColumnsDefs } from './models/columnDefinitions';
import { currentTranslations } from '../../../core/locales/locale';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../state/ApplicationState';
import { TableButtonRenderer } from '../../components/AgGrid/Renderers/TableRemoveButton/TableRemoveButton';
import {
    applyInitialSortFromDisplayPreferences,
    createTachoColumnState,
} from '../../../core/domainModels/displayPreferences/tachoColumnState';
import { modifyDistributorsDisplayPreferences, modifyDistributorsTableFilters } from '../../../state/distributors/reducer';
import AgGridContextMenuWrapper from '../../components/ContextMenu/AgGridContextMenuWrapper';

interface DistributorsTableProps {
    distributors: Array<Distributor>;
    onDistributorDoubleClicked: () => void;
    onSelectionChanged: (distributors: Array<Distributor>) => void;
}
export const DistributorsTable = (props: DistributorsTableProps) => {
    const dispatch = useDispatch();
    const displayPreferences = useSelector((state: ApplicationState) => state.distributors.displayPreferences);
    const tableFiltersState = useSelector((state: ApplicationState) => state.distributors.tableFilters);
    const { formatMessage } = useIntl();
    const gridApi = useRef<GridApi>();

    const columnDefs = useMemo(
        () => distributorsTableColumnsDefinitions(formatMessage, displayPreferences),
        [formatMessage, displayPreferences],
    );

    const previousDistributors = usePrevious(props.distributors);
    useEffect(() => {
        const added = props.distributors.filter(
            distributor => !previousDistributors?.find(prevDistributor => prevDistributor.id === distributor.id),
        );
        const removed = previousDistributors?.filter(
            prevDistributor => !props.distributors?.find(distributor => prevDistributor.id === distributor.id),
        );
        const updated = props.distributors.filter(
            distributor => !added.some(addedDistributor => addedDistributor.id === distributor.id),
        );
        gridApi.current?.applyTransaction({
            add: added,
            remove: removed,
            update: updated,
        });
    }, [props.distributors]);

    const rowsSelectionChanged = (event: SelectionChangedEvent) => {
        const selectedNodes = event.api.getSelectedNodes()?.map(node => node.data as Distributor);
        if (!selectedNodes) {
            console.log('No selected rows.');
            return;
        }

        props.onSelectionChanged(selectedNodes);
    };

    const onGridReady = useCallback(
        (params: GridReadyEvent) => {
            gridApi.current = params.api;
            applyInitialSortFromDisplayPreferences(params, displayPreferences, [{ colId: 'name', sort: 'asc' }]);
            params.api.setFilterModel(tableFiltersState);
            params.api.setGridOption('rowData', props.distributors);
        },
        [props.distributors, displayPreferences, tableFiltersState],
    );

    const sideBarConfiguration = {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivotMode: true,
                    suppressColumnFilter: true,
                    suppressColumnSelectAll: true,
                },
            },
        ],
    };

    function onColumnsChanged(event: AgGridEvent): void {
        const tachoColumnState = createTachoColumnState(event.api.getColumnState());
        dispatch(modifyDistributorsDisplayPreferences(tachoColumnState));
    }
    const onColumnResized = (event: ColumnResizedEvent) => {
        if (event.finished) {
            return onColumnsChanged(event);
        }
    };
    const onTableFiltersStateChanged = (event: FilterChangedEvent) => {
        const filters = event.api.getFilterModel();
        dispatch(modifyDistributorsTableFilters(filters));
    };

    return (
        <>
            <AgGridContextMenuWrapper gridApi={gridApi.current} showStatusBar>
                <AgGridReact
                    suppressPropertyNamesCheck
                    getRowId={params => params.data?.id ?? ''}
                    suppressDragLeaveHidesColumns
                    suppressContextMenu={true}
                    multiSortKey="ctrl"
                    rowSelection="multiple"
                    defaultColDef={distributorsTableDefaultColumnsDefs}
                    columnDefs={columnDefs}
                    modules={[ClientSideRowModelModule]}
                    onGridReady={onGridReady}
                    onSelectionChanged={rowsSelectionChanged}
                    suppressScrollOnNewData={true}
                    sideBar={sideBarConfiguration}
                    localeText={currentTranslations?.values}
                    onRowDoubleClicked={e => {
                        props.onDistributorDoubleClicked();
                    }}
                    components={{ tableButtonRenderer: TableButtonRenderer }}
                    onSortChanged={event => onColumnsChanged(event)}
                    onColumnMoved={event => onColumnsChanged(event)}
                    onColumnResized={event => onColumnResized(event)}
                    onFilterChanged={event => onTableFiltersStateChanged(event)}
                />
            </AgGridContextMenuWrapper>
        </>
    );
};
